import { createSelector } from '@reduxjs/toolkit';

import * as Arr from 'fp-ts/lib/Array';
import * as E from 'fp-ts/lib/Either';
import { pipe } from 'fp-ts/lib/function';
import * as O from 'fp-ts/lib/Option';

import { ConfigurableProperties, ModaSofa } from '@dora/backpack';
import { CurveTemplate, StandardTemplate } from '@dora/backpack/src/modaSofa/template';
import { identity } from 'fp-ts/lib/function';
import { Product } from '../../data/product';
import * as Pr from '../../data/product';
import { RootState } from '../../store';
import * as ConfigurablePropertiesSelectors from './configurableProperties';
import * as Core from './core';
import * as ProductDataSelectors from './productData';

/** All modaSofa Templates for all colours, keyed by Range
 *
 * This is the original data this store holds - the other selectors derive different formats of this
 * data for various purposes
 */
export const unresolvedModularTemplatesByRange = (state: RootState) =>
    state.modaSofaTemplates?.templates;

/** resolves the given array of templates to the single template whose properties match the provided
 * set of global configurable properties
 *
 * if products in a template have no properties, we assume that's a match (non-configurable products
 * should pass)
 *
 * follows the behaviour of Array.prototype.find (if none found returns undefined, if multiple found
 * returns first)
 */
const resolveTemplates = (
    templates: ModaSofa.Template[],
    productBySku: (sku: string) => Product | undefined,
    globalPropSelections: ConfigurableProperties.ConfigurablePropertySelectionT[],
): CurveTemplate | StandardTemplate | undefined => {
    const resolvedTemplates = templates.filter(t =>
        pipe(
            E.Do,
            E.bind('statePropSelections', () => E.right(globalPropSelections)),
            E.bind('product', () =>
                E.fromNullable('Product could not be found')(productBySku(t.sku)),
            ),
            E.bind('productGlobalConfigurableProps', ({ product }) =>
                E.right(
                    pipe(
                        product.configurableProperties,
                        O.fromNullable,
                        O.chain(props => {
                            const productGlobalProps = Object.fromEntries(
                                Object.entries(props).filter(([_, { isGlobal }]) => isGlobal),
                            );
                            if (Object.keys(productGlobalProps).length === 0) {
                                return O.none;
                            }
                            return O.some(productGlobalProps);
                        }),
                    ),
                ),
            ),
            E.bind('productPropSelections', ({ product, productGlobalConfigurableProps }) =>
                pipe(
                    productGlobalConfigurableProps,
                    O.map(p => Object.keys(p)),
                    O.map(
                        Arr.map(propertyKey => {
                            const maybeFinish = product.properties?.[propertyKey];
                            return E.fromNullable(
                                `Product ${Pr.productId(
                                    product,
                                )} internally inconsistent - has global prop ${propertyKey} but no matching field in properties`,
                            )(
                                ConfigurableProperties.isFinish(maybeFinish)
                                    ? { propertyKey, finish: maybeFinish }
                                    : null,
                            );
                        }),
                    ),
                    /**
                     * 1. We now have an Option<Array<Either>>, we want an Either<Option<Array>>
                     * The rest of this pipe accomplishes that conversion
                     *
                     * 2. TypeScript forgot the types after this step for some reason, hence explicit
                     * typing below
                     */
                    O.map<
                        E.Either<string, ConfigurableProperties.ConfigurablePropertySelectionT>[],
                        E.Either<string, ConfigurableProperties.ConfigurablePropertySelectionT[]>
                    >(Arr.sequence(E.Applicative)),
                    O.fold(() => E.right(O.none), E.map(O.some)),
                ),
            ),
            E.map(({ productPropSelections, statePropSelections }) => {
                return pipe(
                    productPropSelections,
                    O.map(
                        Arr.every(productPropSelection => {
                            // If the state has global property selections set but there are none,
                            // then all products must pass
                            if (statePropSelections.length === 0) {
                                return true;
                            }
                            const matchingStateProp = statePropSelections.find(
                                s => s.propertyKey === productPropSelection.propertyKey,
                            );

                            // If the product property does not exist in the state, then we are not
                            // considering this property during matching, thus pass
                            if (!matchingStateProp) {
                                return true;
                            }

                            // Property found in both product and state, check whether they match
                            return ConfigurableProperties.finishesEqual(
                                matchingStateProp.finish,
                                productPropSelection.finish,
                            );
                        }),
                    ),
                );
            }),
            E.fold(
                err => {
                    // Error when attempting to resolve this template
                    // Handle the error here and return false so that the template does not resolve
                    // TODO: consider using an Error enum and sending the error further up to be
                    // handled in a more appropriate location
                    // TODO: We can't dispatch a log message via the normal route here, as this
                    // happens inside a selector which by design cannot dispatch actions.
                    console.warn(
                        `Resolving Modular Sofa Templates based on current global property selections failed: a template failed to process`,
                        {
                            message: err,
                            currentTemplate: t,
                            templatesBeingResolved: templates,
                            globalPropSelections,
                        },
                    );
                    return false;
                },
                O.fold(() => true, identity),
            ),
        ),
    );

    if (resolvedTemplates.length === 0) {
        return undefined;
    }

    // This could happen if e.g. we load a sofa with coloured template options but no global
    // configurable colour properties have been set
    // We do not expect this to ever be the case though.
    if (resolvedTemplates.length !== 1) {
        // TODO: We can't dispatch a log message via the normal route here, as this
        // happens inside a selector which by design cannot dispatch actions.
        console.warn(
            `Resolving Modular Sofa Templates unable to resolve to a single template. Selecting first template in the array. This may result in mixed colours.`,
            {
                allTemplates: templates,
                resolvedTemplates,
                globalPropSelections,
            },
        );
    }

    return resolvedTemplates[0];
};

/** resolve a modular template.
 *
 * resolving is the practice of, given a set of configurable property selections, narrowing down a
 * set of templates into the single template with properties matching those selections.
 */
const resolveModularTemplate = (
    unresolvedModularTemplate: ModaSofa.UnresolvedModularTemplate,
    productBySku: (sku: string) => Product | undefined,
    propSelections: ConfigurableProperties.ConfigurablePropertySelectionT[],
): E.Either<string, ModaSofa.ResolvedCurvedTemplate | ModaSofa.ResolvedStandardTemplate> => {
    if (unresolvedModularTemplate.kind === 'standard') {
        return pipe(
            E.Do,
            E.bind('squareCorner', () => {
                return E.fromNullable('Could not resolve square corner templates')(
                    resolveTemplates(
                        unresolvedModularTemplate.corner.square,
                        productBySku,
                        propSelections,
                    ),
                );
            }),
            E.bind('angledCorner', () => {
                if (unresolvedModularTemplate.corner.angled === undefined) {
                    return E.right(undefined);
                }
                return E.fromNullable('Could not resolve angled corner templates')(
                    resolveTemplates(
                        unresolvedModularTemplate.corner.angled,
                        productBySku,
                        propSelections,
                    ),
                );
            }),
            E.bind('straight', () => {
                return E.fromNullable('Could not resolve straight templates')(
                    resolveTemplates(
                        unresolvedModularTemplate.straight,
                        productBySku,
                        propSelections,
                    ),
                );
            }),
            E.bind('endLeft', () => {
                return E.fromNullable('Could not resolve left end piece templates')(
                    resolveTemplates(
                        unresolvedModularTemplate.end.left,
                        productBySku,
                        propSelections,
                    ),
                );
            }),
            E.bind('endRight', () => {
                return E.fromNullable('Could not resolve right end piece templates')(
                    resolveTemplates(
                        unresolvedModularTemplate.end.right,
                        productBySku,
                        propSelections,
                    ),
                );
            }),
            E.bind('interstitials', () => {
                if (unresolvedModularTemplate.interstitials.drinksCoolerArmrest === undefined) {
                    return E.right({});
                }
                return E.fromNullable('Could not resolve interstitial templates')({
                    drinksCoolerArmrest: resolveTemplates(
                        unresolvedModularTemplate.interstitials.drinksCoolerArmrest,
                        productBySku,
                        propSelections,
                    ),
                });
            }),
            E.map(({ angledCorner, squareCorner, straight, endLeft, endRight, interstitials }) => ({
                corner: { square: squareCorner, angled: angledCorner },
                straight,
                end: { left: endLeft, right: endRight },
                interstitials,
                kind: 'standard',
            })),
        );
    } else {
        return pipe(
            E.Do,
            E.bind('middle', () => {
                return E.fromNullable('Could not resolve middle templates')(
                    resolveTemplates(
                        unresolvedModularTemplate.middle,
                        productBySku,
                        propSelections,
                    ) as CurveTemplate,
                );
            }),
            E.bind('endLeft', () => {
                return E.fromNullable('Could not resolve left end piece templates')(
                    resolveTemplates(
                        unresolvedModularTemplate.end.left,
                        productBySku,
                        propSelections,
                    ) as CurveTemplate,
                );
            }),
            E.bind('endRight', () => {
                return E.fromNullable('Could not resolve right end piece templates')(
                    resolveTemplates(
                        unresolvedModularTemplate.end.right,
                        productBySku,
                        propSelections,
                    ) as CurveTemplate,
                );
            }),
            E.bind('interstitials', () => {
                if (unresolvedModularTemplate.interstitials.drinksCoolerArmrest === undefined) {
                    return E.right({});
                }
                return E.fromNullable('Could not resolve interstitial templates')({
                    drinksCoolerArmrest: resolveTemplates(
                        unresolvedModularTemplate.interstitials.drinksCoolerArmrest,
                        productBySku,
                        propSelections,
                    ) as CurveTemplate,
                });
            }),
            E.map(({ middle, endLeft, endRight, interstitials }) => ({
                end: { left: endLeft, right: endRight },
                interstitials,
                middle: middle,
                kind: 'curved',
            })),
        );
    }
};

/** All resolved modaSofa Templates for the current global configurable properties, keyed by Range.
 */
export const templates = createSelector(
    unresolvedModularTemplatesByRange,
    ConfigurablePropertiesSelectors.currentPropertySelections,
    ProductDataSelectors.productBySkuFn,
    (
        unresolvedTemplatesByRange,
        globalConfigurablePropSelections,
        productBySku,
    ): Record<
        string,
        ModaSofa.ResolvedCurvedTemplate | ModaSofa.ResolvedStandardTemplate
    > | null => {
        if (!unresolvedTemplatesByRange || !globalConfigurablePropSelections || !productBySku) {
            return null;
        }

        // Build up the object to return
        const resolvedTemplatesByRange: Record<
            string,
            ModaSofa.ResolvedCurvedTemplate | ModaSofa.ResolvedStandardTemplate
        > = {};

        Object.entries(unresolvedTemplatesByRange).forEach(([range, unresolvedTemplate]) => {
            pipe(
                unresolvedTemplate,
                E.fromNullable('sofa templates not yet initialised'),
                E.chain(unresolvedT =>
                    resolveModularTemplate(
                        unresolvedT,
                        productBySku,
                        globalConfigurablePropSelections,
                    ),
                ),
                E.fold(
                    err => {
                        // TODO: We can't dispatch a log message via the normal route here, as this
                        // happens inside a selector which by design cannot dispatch actions.
                        console.warn(
                            'Could not resolve given modular template to the current set of global properties',
                            {
                                unresolvedTemplate,
                                globalConfigurablePropSelections,
                                errorMessage: err,
                            },
                        );
                    },
                    resolvedTemplate => {
                        // Only ranges for which a valid resolved template can be found will be
                        // added to the return object
                        resolvedTemplatesByRange[range] = resolvedTemplate;
                    },
                ),
            );
        });

        console.log('templates selector has resolved templates', { resolvedTemplatesByRange });

        return resolvedTemplatesByRange;
    },
);

/**
 * Get a single resolved Moda Sofa template based on the current range
 *
 * The template can be undefined if there is no range set
 */
export const templatesForCurrentRange = createSelector(
    templates,
    Core.range,
    // if range has not been set yet, then there is no sensible choice of 'default' pieces. Must
    // therefore return null
    (templates, range) => {
        if (!range || !templates) {
            return null;
        }
        return templates[range];
    },
);

/**
 * Checks if current range has no modular sofa templates
 */
export const currentRangeHasTemplates = createSelector(
    templatesForCurrentRange,
    templates => !!templates,
);

/**
 * Find if the current range supports interstitial sofa pieces or not
 */
export const rangeSupportsInterstitials = createSelector(templatesForCurrentRange, t =>
    t === null ? null : Object.keys(t.interstitials).length > 0,
);

/**
 * Get a modular sofa configurator based on the current range
 *
 * the configurator can be undefined if there is no range set
 */
export const currentConfigurator = createSelector(Core.range, templateRange => {
    if (!templateRange) {
        return null;
    } else {
        return (
            size: ModaSofa.Size.Size,
            interstitialPlacements: ModaSofa.InterstitialsT,
            cornerStyle: ModaSofa.CornerStyleT,
        ): ModaSofa.Configuration => ({
            templateKey: templateRange,
            size,
            interstitialPlacements,
            cornerStyle,
        });
    }
});
